/**
 * Detect meeting / web URLs stored in calendar location fields so the UI can
 * open Zoom, Teams, Meet, and plain https links instead of showing dead text.
 */

const ABSOLUTE_URL_RE =
  /^(?:https?:\/\/|zoommtg:\/\/|msteams:\/\/)\S+$/i;

const EMBEDDED_URL_RE =
  /(?:https?:\/\/|zoommtg:\/\/|msteams:\/\/)[^\s<>"']+/i;

const BARE_WWW_RE = /^www\.\S+$/i;

export interface LocationLink {
  href: string;
  label: string;
}

/** Prefer a short join label over a raw Zoom/Teams URL when that is the whole location. */
export function meetingLinkLabel(href: string): string {
  try {
    if (/^https?:\/\//i.test(href)) {
      const host = new URL(href).hostname.toLowerCase();
      if (host === 'zoom.us' || host.endsWith('.zoom.us')) return 'Join Zoom meeting';
      if (
        host === 'teams.microsoft.com' ||
        host.endsWith('.teams.microsoft.com') ||
        host === 'teams.live.com'
      ) {
        return 'Join Teams meeting';
      }
      if (host === 'meet.google.com') return 'Join Google Meet';
      if (host === 'meet.jit.si') return 'Join meeting';
    }
  } catch {
    // Fall through to the raw href.
  }
  if (/^zoommtg:/i.test(href)) return 'Join Zoom meeting';
  if (/^msteams:/i.test(href)) return 'Join Teams meeting';
  return href;
}

/** Normalize a location string into a navigable link when it is (or contains) a URL. */
export function parseLocationLink(location: string): LocationLink | null {
  const trimmed = location.trim();
  if (!trimmed) return null;

  if (ABSOLUTE_URL_RE.test(trimmed)) {
    return { href: trimmed, label: meetingLinkLabel(trimmed) };
  }

  if (BARE_WWW_RE.test(trimmed)) {
    const href = `https://${trimmed}`;
    return { href, label: meetingLinkLabel(href) };
  }

  const embedded = trimmed.match(EMBEDDED_URL_RE)?.[0];
  if (embedded) {
    // Location is mostly a URL with a short prefix/suffix, treat the URL as the action.
    const leftover = trimmed.replace(embedded, '').trim();
    if (!leftover || leftover.length < 24) {
      return { href: embedded, label: meetingLinkLabel(embedded) };
    }
    return { href: embedded, label: trimmed };
  }

  return null;
}
